package com.yet.tetris.domain.usecase

import com.yet.tetris.domain.model.game.GameState
import com.yet.tetris.domain.model.settings.GestureSensitivity
import kotlin.math.abs

/**
 * Use case for handling swipe gestures on mobile platforms.
 * Interprets swipe direction and velocity to determine the appropriate game action.
 */
class HandleSwipeInputUseCase(
    private val movePiece: MovePieceUseCase,
    private val hardDrop: HardDropUseCase,
) {
    data class Result(
        val state: GameState,
        val action: SwipeAction,
    )

    companion object {
        private const val HARD_DROP_THRESHOLD = 0.5f
    }

    /**
     * Represents the result of processing a swipe gesture.
     */
    sealed class SwipeAction {
        object MoveLeft : SwipeAction()

        object MoveRight : SwipeAction()

        object SoftDrop : SwipeAction()

        object HardDrop : SwipeAction()

        object None : SwipeAction()
    }

    /**
     * Processes a swipe gesture and returns the updated game state.
     *
     * @param state Current game state
     * @param deltaX Horizontal swipe distance (positive = right, negative = left)
     * @param deltaY Vertical swipe distance (positive = down)
     * @param velocityX Horizontal swipe velocity
     * @param velocityY Vertical swipe velocity
     * @return Updated GameState or null if no valid action
     */
    operator fun invoke(
        state: GameState,
        deltaX: Float,
        deltaY: Float,
        velocityX: Float,
        velocityY: Float,
        gestureSensitivity: GestureSensitivity = GestureSensitivity.NORMAL,
    ): Result? {
        val action = determineSwipeAction(deltaX, deltaY, velocityX, velocityY, gestureSensitivity)

        val newState =
            when (action) {
                SwipeAction.MoveLeft -> movePiece.moveLeft(state).appliedStateOrNull()
                SwipeAction.MoveRight -> movePiece.moveRight(state).appliedStateOrNull()
                SwipeAction.SoftDrop -> movePiece.moveDown(state).appliedStateOrNull()
                SwipeAction.HardDrop -> hardDrop(state)
                SwipeAction.None -> null
            }

        return newState?.let { Result(it, action) }
    }

    /**
     * Determines the action based on swipe direction and velocity.
     * Vertical swipes: slow = soft drop, fast = hard drop
     * Horizontal swipes: move left or right
     */
    private fun determineSwipeAction(
        deltaX: Float,
        deltaY: Float,
        velocityX: Float,
        velocityY: Float,
        gestureSensitivity: GestureSensitivity,
    ): SwipeAction {
        val absX = abs(deltaX)
        val absY = abs(deltaY)
        val normalizedVelocity = abs(velocityY) / gestureSensitivity.velocityMultiplier

        // Determine primary direction
        return if (absX > absY) {
            // Horizontal swipe
            if (deltaX > 0) SwipeAction.MoveRight else SwipeAction.MoveLeft
        } else if (absY > absX) {
            // Vertical swipe - check velocity to determine soft vs hard drop
            if (normalizedVelocity > HARD_DROP_THRESHOLD) {
                SwipeAction.HardDrop
            } else {
                SwipeAction.SoftDrop
            }
        } else {
            SwipeAction.None
        }
    }

    /**
     * Handles a tap gesture (used for rotation).
     */
    fun handleTap(state: GameState): SwipeAction {
        // Tap is handled separately and triggers rotation
        // This is just a marker - actual rotation is handled by RotatePieceUseCase
        return SwipeAction.None
    }

    private fun MovePieceUseCase.Result.appliedStateOrNull(): GameState? =
        when (this) {
            is MovePieceUseCase.Result.Applied -> gameState
            is MovePieceUseCase.Result.Blocked -> null
        }
}
